import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";

const empresaId = localStorage.getItem("empresaId") || "1";

function StatCard({ title, value, icon, color, loading }) {
  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="button" color="text" fontWeight="regular">
              {title}
            </MDTypography>
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <MDTypography variant="h4" fontWeight="medium">
                {value}
              </MDTypography>
            )}
          </MDBox>
          <MDBox
            variant="gradient"
            bgColor={color}
            color="white"
            width="4rem"
            height="4rem"
            borderRadius="md"
            display="flex"
            justifyContent="center"
            alignItems="center"
            shadow="md"
          >
            <Icon fontSize="large">{icon}</Icon>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  loading: PropTypes.bool,
};

export default function ReportesCompras() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [fechaInicio, setFechaInicio] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split("T")[0];
  });
  const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    estadisticas: {
      totalCompras: 0,
      montoTotal: 0,
      proveedoresActivos: 0,
      promedioCompra: 0,
    },
    proveedoresTop: [],
    productosMasComprados: [],
  });

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.REPORTES.COMPRAS, {
        params: {
          empresa_id: empresaId,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });
      setReportData(response.data);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const { estadisticas, proveedoresTop, productosMasComprados } = reportData;

  return (
    <MDBox>
      {/* Filtros */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <TextField
            type="date"
            label="Fecha Inicio"
            fullWidth
            variant="outlined"
            size="small"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            type="date"
            label="Fecha Fin"
            fullWidth
            variant="outlined"
            size="small"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4} display="flex" gap={2}>
          <MDButton
            variant="gradient"
            color={sidenavColor}
            fullWidth
            onClick={fetchReportData}
            disabled={loading}
          >
            <Icon sx={{ mr: 1 }}>refresh</Icon>
            {loading ? "Actualizando..." : "Actualizar"}
          </MDButton>
          <MDButton variant="outlined" color={sidenavColor} fullWidth>
            <Icon sx={{ mr: 1 }}>file_download</Icon>
            Exportar
          </MDButton>
        </Grid>
      </Grid>

      {/* Estadísticas Generales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total de Compras"
            value={estadisticas.totalCompras}
            icon="shopping_cart"
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Monto Total"
            value={`$${estadisticas.montoTotal.toLocaleString("es-MX", {
              minimumFractionDigits: 2,
            })}`}
            icon="attach_money"
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Proveedores Activos"
            value={estadisticas.proveedoresActivos}
            icon="business"
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Promedio por Compra"
            value={`$${estadisticas.promedioCompra.toLocaleString("es-MX", {
              minimumFractionDigits: 2,
            })}`}
            icon="trending_up"
            color="warning"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Tablas de Reportes */}
      <Grid container spacing={3}>
        {/* Top Proveedores */}
        <Grid item xs={12} md={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>leaderboard</Icon>
                Top 5 Proveedores
              </MDTypography>
              <MDBox>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={3}>
                    <CircularProgress color="inherit" />
                  </MDBox>
                ) : proveedoresTop.length === 0 ? (
                  <MDTypography variant="button" color="text">No hay datos disponibles</MDTypography>
                ) : (
                  proveedoresTop.map((prov, index) => (
                    <MDBox
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1.5}
                      borderBottom={index < proveedoresTop.length - 1 ? "1px solid #f0f0f0" : ""}
                    >
                      <MDBox>
                        <MDTypography variant="button" fontWeight="medium">
                          {index + 1}. {prov.nombre}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {prov.compras} órdenes
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="h6" color={sidenavColor}>
                        ${parseFloat(prov.total).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                      </MDTypography>
                    </MDBox>
                  ))
                )}
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Productos Más Comprados */}
        <Grid item xs={12} md={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>inventory_2</Icon>
                Productos Más Comprados
              </MDTypography>
              <MDBox>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={3}>
                    <CircularProgress color="inherit" />
                  </MDBox>
                ) : productosMasComprados.length === 0 ? (
                  <MDTypography variant="button" color="text">No hay datos disponibles</MDTypography>
                ) : (
                  productosMasComprados.map((prod, index) => (
                    <MDBox
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={1.5}
                      borderBottom={
                        index < productosMasComprados.length - 1 ? "1px solid #f0f0f0" : ""
                      }
                    >
                      <MDBox>
                        <MDTypography variant="button" fontWeight="medium">
                          {index + 1}. {prod.nombre}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          Cantidad: {prod.cantidad}
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="h6" color={sidenavColor}>
                        ${parseFloat(prod.monto).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                      </MDTypography>
                    </MDBox>
                  ))
                )}
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
